// Reset password — consumes a token from the email link.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { useDict } from "@/i18n/LocaleProvider";
import { api, ApiError } from "@/lib/api";

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";
  const t = useDict().app.auth;

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // If there's no token in the URL, bounce back to login after a moment.
  useEffect(() => {
    if (!token) {
      const t = setTimeout(() => navigate("/login"), 1500);
      return () => clearTimeout(t);
    }
  }, [token, navigate]);

  async function submit() {
    setErr(null);
    if (pwd.length < 8) {
      setErr(t.errPwdShort);
      return;
    }
    if (pwd !== confirm) {
      setErr(t.errPwdMismatch);
      return;
    }
    setBusy(true);
    try {
      await api("/auth/reset-password", {
        method: "POST",
        body: { token, new_password: pwd },
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-5">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TrueHacking" className="h-20 w-auto mb-4 mx-auto" />
          <div className="font-display text-[32px] font-bold text-accent tracking-[0.14em]">
            TRUEHACKING
          </div>
          <div className="font-mono text-[10px] text-dim tracking-[0.1em] mt-1 uppercase">
            {t.resetSubtitle}
          </div>
        </div>

        <Card className="!p-5 sm:!p-7">
          {!token ? (
            <div className="font-mono text-[11px] text-danger">
              {t.missingToken}
            </div>
          ) : done ? (
            <>
              <div className="font-display text-[18px] font-semibold text-content mb-2">
                {t.pwdUpdatedTitle}
              </div>
              <div className="font-mono text-[11px] text-muted leading-[1.6] mb-3">
                {t.pwdUpdatedBody}
              </div>
              <Link to="/login" className="text-accent font-mono text-[11px] underline">
                {t.signInNow}
              </Link>
            </>
          ) : (
            <>
              <div className="font-display text-[18px] font-semibold text-content mb-1">
                {t.chooseNewPwd}
              </div>
              <div className="font-mono text-[11px] text-muted mb-5">
                {t.chooseNewPwdSub}
              </div>

              <div
                onKeyDown={(e) => {
                  if (e.key === "Enter" && pwd && confirm && !busy) submit();
                }}
              >
                <Input
                  label={t.newPassword}
                  value={pwd}
                  onChange={setPwd}
                  type="password"
                  placeholder={t.passwordPlaceholder}
                />
                <Input
                  label={t.confirmPassword}
                  value={confirm}
                  onChange={setConfirm}
                  type="password"
                  placeholder={t.passwordPlaceholder}
                />
              </div>

              {err && (
                <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-3">
                  {err}
                </div>
              )}

              <Button
                variant="primary"
                full
                onClick={submit}
                disabled={busy || !pwd || !confirm}
              >
                {busy ? t.busy : t.updatePassword}
              </Button>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-accent font-mono text-[11px] underline"
                >
                  ← {t.backToSignin}
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
